import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Manual (no autoplay) photo carousel for the public product pages (QR + Catalog).
 * - Single photo  -> plain contained image, no controls.
 * - Multiple photos -> horizontal swipeable carousel with dot indicators.
 * Images are contained via a fixed 4/5 aspect ratio + object-fit: cover (no distortion).
 */
export default function PhotoCarousel({ photos, apiBase, alt = "" }) {
  const list = photos || [];
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false, align: "start", dragFree: false });
  const [selected, setSelected] = useState(0);

  const scrollTo = useCallback((i) => emblaApi && emblaApi.scrollTo(i), [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelected(emblaApi.selectedScrollSnap());
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    return () => { emblaApi.off("select", onSelect); emblaApi.off("reInit", onSelect); };
  }, [emblaApi]);

  if (list.length === 0) return null;

  if (list.length === 1) {
    return (
      <div className="pp-hero" data-testid="photo-single">
        <img src={`${apiBase}${list[0].url}`} alt={alt} loading="eager" />
      </div>
    );
  }

  return (
    <div className="pp-carousel" data-testid="photo-carousel">
      <div className="pp-carousel__viewport" ref={emblaRef}>
        <div className="pp-carousel__container">
          {list.map((ph, i) => (
            <div className="pp-carousel__slide" key={ph.id || i}>
              <img src={`${apiBase}${ph.url}`} alt={alt} draggable={false}
                   loading={i === 0 ? "eager" : "lazy"} />
            </div>
          ))}
        </div>
      </div>

      <button type="button" className="pp-carousel__arrow pp-carousel__arrow--prev"
              aria-label="Foto precedente" onClick={() => scrollTo(Math.max(0, selected - 1))}
              disabled={selected === 0} data-testid="photo-carousel-prev">
        <ChevronLeft className="w-4 h-4" strokeWidth={2} />
      </button>
      <button type="button" className="pp-carousel__arrow pp-carousel__arrow--next"
              aria-label="Foto successiva" onClick={() => scrollTo(Math.min(list.length - 1, selected + 1))}
              disabled={selected === list.length - 1} data-testid="photo-carousel-next">
        <ChevronRight className="w-4 h-4" strokeWidth={2} />
      </button>

      <div className="pp-carousel__dots" data-testid="photo-carousel-dots">
        {list.map((_, i) => (
          <button key={i} type="button" aria-label={`Vai alla foto ${i + 1}`}
                  className={`pp-carousel__dot ${i === selected ? "is-active" : ""}`}
                  onClick={() => scrollTo(i)} data-testid={`photo-carousel-dot-${i}`} />
        ))}
      </div>
    </div>
  );
}
